import React from 'react';
import { defaultTo } from 'lodash';
import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { Box, Flex, xcss, Text, Inline } from '@atlaskit/primitives';
import {
  TermPaymentTypes,
  TransactionAccount,
  TermDetailsAccountConnection,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  FormattedAddress,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  useFeatureToggle,
  CustomPanel,
} from '@css-xp/base';
import { getTaxIdLabels } from '@css-xp/ppc-tax-component';
import { FormatCurrency, NoData } from '@css-xp/ppc-common';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import './credit-details-summary-section.scss';

interface CreditDetailsSummarySectionProps {
  transactionAccount: TransactionAccount | undefined;
  hasPayByStatement: boolean;
  isLoading?: boolean;
  associatedBillingAccounts?: TermDetailsAccountConnection | null;
  isMultiCurrencyChangesEnabled?: boolean;
}

const LoadingValueIndicator = () => (
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="200px" />
  </SkeletonLoaderWrapper>
);

export const CreditDetailsSummarySection = ({
  transactionAccount,
  hasPayByStatement,
  associatedBillingAccounts,
  isLoading,
  isMultiCurrencyChangesEnabled,
}: CreditDetailsSummarySectionProps) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const billingAccountsList = isMultiCurrencyChangesEnabled
    ? associatedBillingAccounts
    : transactionAccount?.billingAccountsList;
  const currency = billingAccountsList?.currency;
  const totalCreditLimit = billingAccountsList?.totalCreditLimit;
  const availableCreditLimit = billingAccountsList?.availableCreditLimit;
  const creditOutstanding = billingAccountsList?.creditOutstanding;
  const pendingInvoicesTotal = billingAccountsList?.nodes?.reduce(
    (acc, obj) => acc + (obj?.pendingInvoiceRequestsTotal ?? 0),
    0
  );
  const pendingInvoiceRequestsTotal = defaultTo(pendingInvoicesTotal, 0);
  const taxIdLabels = getTaxIdLabels(transactionAccount?.billToParty);
  const termPaymentMethod = billingAccountsList?.paymentMethod;

  const showTotalLimitAndAvailableCreditSection = !hasPayByStatement;
  const showTotalCreditUsedSection = termPaymentMethod === TermPaymentTypes.NET30 || hasPayByStatement;
  const showPendingInvoiceInfo = pendingInvoiceRequestsTotal !== 0;
  const totalCreditLabel = 'Total credit limit';
  const totalCreditUsedLabel = 'Total credit used';
  const availableCreditLabel = 'Available credit';
  const billingAddressLabel = 'Billing address';
  const billingAddressValue = (
    <FormattedAddress
      addressLine1={transactionAccount?.billToParty?.addressLine1}
      addressLine2={transactionAccount?.billToParty?.addressLine2}
      city={transactionAccount?.billToParty?.city}
      country={transactionAccount?.billToParty?.country}
      postcode={transactionAccount?.billToParty?.postcode}
      state={transactionAccount?.billToParty?.state}
    />
  );
  const inflightInvoiceAmount = (
    <FormatCurrency currency={currency} value={pendingInvoiceRequestsTotal} showCurrencyCode showCommas />
  );
  const totalCreditLimitValue =
    totalCreditLimit !== null ? (
      <div className="ppc-term-detail-total-credit-limit">
        <FormatCurrency value={totalCreditLimit} currency={currency} showCurrencyCode showCommas />
      </div>
    ) : (
      <NoData />
    );
  const totalCreditUsedValue = creditOutstanding ? (
    <div className="ppc-term-detail-total-credit-limit-consumed">
      <FormatCurrency value={creditOutstanding} currency={currency} showCurrencyCode showCommas />
    </div>
  ) : (
    <NoData />
  );
  const availableCreditValue =
    availableCreditLimit !== null ? (
      <div className="ppc-term-detail-total-available-limit">
        <FormatCurrency currency={currency} value={availableCreditLimit?.toFixed(2)} showCurrencyCode showCommas />
      </div>
    ) : (
      <NoData />
    );

  if (isDesignIteration1Enabled) {
    const boxStyles = xcss({
      backgroundColor: 'elevation.surface',
      borderColor: 'color.border',
      borderWidth: 'border.width',
      borderStyle: 'solid',
      borderRadius: 'border.radius.200',
      flexGrow: 1,
    });

    const ValueWrapper = styled.span`
      font: ${token('font.heading.large')};
      font-weight: ${token('font.weight.medium')};
      padding-top: ${token('space.050')};
      padding-bottom: ${token('space.100')};
    `;

    const renderBox = (label: string, value: React.ReactNode, showPendingInvoiceText?: boolean) => {
      return (
        <Box padding="space.200" xcss={boxStyles}>
          <Flex direction="column">
            <Text color="color.text.subtlest" size="small">
              {label}
            </Text>
            <ValueWrapper>{isLoading ? <LoadingValueIndicator /> : value}</ValueWrapper>
            {showPendingInvoiceInfo && showPendingInvoiceText && (
              <Text color="color.text.subtle">Includes invoices in progress worth {inflightInvoiceAmount}</Text>
            )}
          </Flex>
        </Box>
      );
    };

    const renderBillingDetailsSection = () => {
      const sectionStyles = xcss({
        marginTop: 'space.200',
      });
      const Wrapper = styled.div`
        margin-top: ${token('space.100')};
      `;

      const rowValue = (
        <>
          {transactionAccount?.billToParty?.name && (
            <Text weight="semibold">{transactionAccount?.billToParty?.name}</Text>
          )}
          <Wrapper>{billingAddressValue}</Wrapper>
          {taxIdLabels?.length > 0 && (
            <Wrapper>
              {taxIdLabels.map((taxIdLabel, index) => (
                <Flex key={index}>Tax ID:&nbsp;{taxIdLabel?.value}</Flex>
              ))}
            </Wrapper>
          )}
        </>
      );

      return (
        <Box testId="billing-and-tax-details" xcss={sectionStyles}>
          <CustomPanel
            expandedHeader="Billing and tax details"
            collapsedHeader="Billing and tax details"
            version={2}
            isExpanded
          >
            <Box padding="space.250" paddingBlockStart="space.150">
              <SummaryPanelContainer version={2}>
                <SummaryPanelRow label={billingAddressLabel} value={rowValue} useWrapper={true} />
              </SummaryPanelContainer>
            </Box>
          </CustomPanel>
        </Box>
      );
    };

    return (
      <>
        <Inline grow="fill" space="space.200" alignBlock="stretch">
          {showTotalLimitAndAvailableCreditSection && renderBox(totalCreditLabel, totalCreditLimitValue)}
          {showTotalCreditUsedSection && renderBox(totalCreditUsedLabel, totalCreditUsedValue, true)}
          {showTotalLimitAndAvailableCreditSection && renderBox(availableCreditLabel, availableCreditValue)}
        </Inline>
        {renderBillingDetailsSection()}
      </>
    );
  }

  return (
    <div className="credit-details-summary-section">
      <div className="tax-details-sub-section-container">
        <SummaryPanelContainer title="Tax details" iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}>
          {taxIdLabels &&
            taxIdLabels?.map((taxIdLabel, index) => (
              <SummaryPanelRow
                label="Tax ID"
                value={taxIdLabel?.value}
                key={`tax-id-label-${index}`}
                testId="display-tax-id"
              />
            ))}
          <SummaryPanelRow label={billingAddressLabel} value={billingAddressValue} testId="display-billing-address" />
        </SummaryPanelContainer>
      </div>
      <span className="containers-divider"></span>
      <div className="payment-term-limits-sub-section-container">
        <SummaryPanelContainer title="Payment terms limits" iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}>
          {showTotalLimitAndAvailableCreditSection && (
            <SummaryPanelRow
              label={totalCreditLabel}
              value={isLoading ? <LoadingValueIndicator /> : totalCreditLimitValue}
              testId="display-total-credit-limit"
            />
          )}
          {showTotalCreditUsedSection && (
            <SummaryPanelRow
              label={totalCreditUsedLabel}
              value={isLoading ? <LoadingValueIndicator /> : totalCreditUsedValue}
              testId="display-total-credit-limit-consumed"
            />
          )}
          {showTotalLimitAndAvailableCreditSection && (
            <SummaryPanelRow
              label={availableCreditLabel}
              value={isLoading ? <LoadingValueIndicator /> : availableCreditValue}
              testId="display-total-available-limit"
            />
          )}
          {showPendingInvoiceInfo && (
            <SummaryPanelRow
              value={
                <>
                  <span className="inflight-invoice-content">
                    Adjusted for {inflightInvoiceAmount} inflight invoices
                  </span>
                </>
              }
              testId="display-inflight-invoice-amount"
            />
          )}
        </SummaryPanelContainer>
      </div>
    </div>
  );
};
